import { useState, useCallback, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_AUTH_BACKEND_URL;

/** Run statuses that mean the run has finished server-side. */
export const TERMINAL_RUN_STATUSES = ['completed', 'failed', 'cancelled'];

/** Maximum SSE reconnect attempts after a dropped stream. */
const MAX_STREAM_RETRIES = 3;

/** Base reconnect backoff (ms); doubles per attempt: 1s, 2s, 4s. */
const STREAM_RETRY_BASE_MS = 1000;

/**
 * Hook providing all workflow CRUD operations and multi-run management.
 *
 * Tracks one active run per workflow in the ``activeRuns`` map.  Because
 * the backend enforces a single-concurrent-run constraint per workflow,
 * multiple *different* workflows can execute in parallel while the same
 * workflow can only run once at a time.  Each active run opens its own
 * SSE connection; connections are stored in a ref-keyed map so they
 * survive re-renders without triggering effects.
 *
 * @returns {object} Workflow state and operation callbacks.
 */
export function useWorkflow() {
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /**
   * One run record per workflow.
   * Shape: { [workflowId]: { runId, status, node_results_json,
   *                          output_json, error_message } }
   */
  const [activeRuns, setActiveRuns] = useState({});

  /** SSE connections keyed by workflowId. */
  const esMapRef = useRef({});

  /** Pending reconnect setTimeout ids keyed by workflowId. */
  const retryTimersRef = useRef({});

  /**
   * Ownership tokens keyed by workflowId.  Each stream (or its
   * error-recovery chain) holds a unique token; async steps compare
   * against the current token and abort when it changed — i.e. when
   * the stream was stopped (cancel/clear/unmount) or superseded by a
   * newer ``startStream`` call.
   */
  const streamTokensRef = useRef({});

  /**
   * Pending-cancel flags: set to true when the user clicks Cancel
   * before the run_id has been returned from the server.  Checked
   * after the POST /runs response resolves so the cancel is applied
   * immediately once a run_id is available.
   */
  const pendingCancelRef = useRef({});

  // ── Internal SSE helpers ────────────────────────────────────────

  /**
   * Close the SSE connection for one workflow (or all when omitted),
   * cancel any pending reconnect timer, and invalidate the stream's
   * ownership token so in-flight recovery steps abort.
   *
   * @param {string} [workflowId] - Workflow to disconnect.  Omit to
   *   close every open connection (e.g. on component unmount).
   */
  const stopStream = useCallback((workflowId) => {
    if (workflowId !== undefined) {
      const es = esMapRef.current[workflowId];
      if (es) {
        es.close();
        delete esMapRef.current[workflowId];
      }
      const timer = retryTimersRef.current[workflowId];
      if (timer !== undefined) {
        clearTimeout(timer);
        delete retryTimersRef.current[workflowId];
      }
      delete streamTokensRef.current[workflowId];
    } else {
      Object.values(esMapRef.current).forEach((es) => es.close());
      esMapRef.current = {};
      Object.values(retryTimersRef.current).forEach((t) =>
        clearTimeout(t),
      );
      retryTimersRef.current = {};
      streamTokensRef.current = {};
    }
  }, []);

  // Close all streams and cancel all reconnect timers on unmount so
  // no reconnect fires (and no state update runs) after teardown.
  useEffect(() => () => stopStream(), [stopStream]);

  /**
   * Open an SSE stream for a specific (workflowId, runId) pair.
   * Updates ``activeRuns[workflowId]`` as events arrive.
   *
   * A dropped connection (proxy idle-timeout, backend stream
   * deadline) does NOT mean the run failed: on error the run status
   * is reconciled against ``GET .../runs/{runId}``.  If the server
   * reports a terminal status that truth is applied; otherwise the
   * stream reconnects with bounded exponential backoff
   * (``MAX_STREAM_RETRIES`` attempts).  Only when retries are
   * exhausted AND the status endpoint is unreachable does the run
   * fall back to ``failed``.
   *
   * @param {string} workflowId
   * @param {string} runId
   */
  const startStream = useCallback(
    (workflowId, runId) => {
      /**
       * Apply a server-fetched run record to ``activeRuns``,
       * mirroring the shape of the SSE ``done`` event.
       *
       * @param {object} run - Run dict from the status endpoint.
       */
      const applyServerRun = (run) => {
        setActiveRuns((prev) => {
          const curr = prev[workflowId] || {};
          return {
            ...prev,
            [workflowId]: {
              ...curr,
              status: run.status,
              output_json: run.output_json ?? curr.output_json ?? null,
              node_results_json:
                run.node_results_json ||
                curr.node_results_json ||
                {},
              error_message: run.error_message || null,
            },
          };
        });
      };

      /**
       * Reconcile a dropped stream against the server: fetch the
       * run's current status, apply terminal truth, or schedule a
       * bounded reconnect while the run is still in flight.
       *
       * @param {number} attempt - Reconnect attempts already made.
       * @param {symbol} token - Ownership token of this chain.
       */
      const reconcile = async (attempt, token) => {
        if (streamTokensRef.current[workflowId] !== token) return;
        let run = null;
        try {
          const res = await fetch(
            `${API_BASE}/workflows/${workflowId}/runs/${runId}`,
            { credentials: 'include' },
          );
          if (res.ok) run = await res.json();
        } catch (_) {
          run = null;
        }
        // Abort if the stream was stopped or superseded while the
        // status fetch was in flight (cancel, clear, unmount, or a
        // newer startStream call).
        if (streamTokensRef.current[workflowId] !== token) return;

        if (run && TERMINAL_RUN_STATUSES.includes(run.status)) {
          delete streamTokensRef.current[workflowId];
          applyServerRun(run);
          return;
        }
        if (attempt < MAX_STREAM_RETRIES) {
          // Run still pending/running (or status unknown): keep the
          // current UI status and reconnect after a backoff.
          const delay = STREAM_RETRY_BASE_MS * 2 ** attempt;
          retryTimersRef.current[workflowId] = setTimeout(() => {
            delete retryTimersRef.current[workflowId];
            openStream(attempt + 1);
          }, delay);
          return;
        }
        // Retries exhausted: apply the server's last word; only when
        // even the status endpoint is unreachable mark it failed.
        delete streamTokensRef.current[workflowId];
        if (run) {
          applyServerRun(run);
          return;
        }
        setActiveRuns((prev) => {
          const curr = prev[workflowId];
          if (!curr) return prev;
          return {
            ...prev,
            [workflowId]: { ...curr, status: 'failed' },
          };
        });
      };

      /**
       * Open one EventSource for the run and wire its handlers.
       *
       * @param {number} attempt - Reconnect attempts already made;
       *   reset to 0 once the stream delivers an event (a healthy
       *   stream earns future drops a fresh retry budget).
       */
      const openStream = (attempt) => {
        stopStream(workflowId);
        const token = Symbol('workflow-stream');
        streamTokensRef.current[workflowId] = token;
        const url =
          `${API_BASE}/workflows/${workflowId}/runs/${runId}/stream`;
        const es = new EventSource(url, { withCredentials: true });
        esMapRef.current[workflowId] = es;
        let received = false;

        es.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            received = true;

            if (data.type === 'status') {
              setActiveRuns((prev) => ({
                ...prev,
                [workflowId]: {
                  ...(prev[workflowId] || {}),
                  status: data.status,
                },
              }));
            } else if (data.type === 'node_result') {
              const { node_id: nodeId, ...result } = data;
              setActiveRuns((prev) => {
                const curr = prev[workflowId] || {};
                return {
                  ...prev,
                  [workflowId]: {
                    ...curr,
                    node_results_json: {
                      ...(curr.node_results_json || {}),
                      [nodeId]: result,
                    },
                  },
                };
              });
            } else if (data.type === 'done') {
              stopStream(workflowId);
              setActiveRuns((prev) => ({
                ...prev,
                [workflowId]: {
                  ...(prev[workflowId] || {}),
                  status: data.status,
                  output_json: data.output,
                  node_results_json:
                    data.node_results ||
                    (prev[workflowId] || {}).node_results_json ||
                    {},
                  error_message: data.error_message || null,
                },
              }));
            }
          } catch (_) {
            // Ignore malformed events
          }
        };

        es.onerror = () => {
          // Close only the socket; the token stays live so this
          // recovery chain keeps ownership of the run.
          es.close();
          if (esMapRef.current[workflowId] === es) {
            delete esMapRef.current[workflowId];
          }
          reconcile(received ? 0 : attempt, token);
        };
      };

      openStream(0);
    },
    [stopStream],
  );

  // ── Workflow CRUD ───────────────────────────────────────────────

  const listWorkflows = useCallback(async () => {
    setLoadingWorkflows(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/workflows?include_templates=true&limit=50`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.workflows || [];
      setWorkflows(list);
      return list;
    } catch (e) {
      setError(e.message);
      return [];
    } finally {
      setLoadingWorkflows(false);
    }
  }, []);

  const createWorkflow = useCallback(
    async (name, description = '', icon = null, dagJson = null) => {
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/workflows`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name,
            description,
            dag_json: dagJson ?? { nodes: [], edges: [] },
            icon,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        return await res.json();
      } catch (e) {
        setError(e.message);
        return null;
      }
    },
    [],
  );

  const getWorkflow = useCallback(async (workflowId) => {
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${workflowId}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, []);

  const saveWorkflow = useCallback(async (workflowId, fields) => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${workflowId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(fields),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  const deleteWorkflow = useCallback(async (workflowId) => {
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${workflowId}`,
        { method: 'DELETE', credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setWorkflows((prev) =>
        prev.filter((w) => w.id !== workflowId),
      );
      return true;
    } catch (e) {
      setError(e.message);
      return false;
    }
  }, []);

  // ── Run management ──────────────────────────────────────────────

  /**
   * Start a new workflow run.  Opens an SSE stream immediately after
   * the backend returns the ``run_id``.
   *
   * @param {string} workflowId
   * @param {object} [inputValues={}]
   * @returns {Promise<string|null>} run_id or null on error
   */
  const runWorkflow = useCallback(
    async (workflowId, inputValues = {}) => {
      setActiveRuns((prev) => ({
        ...prev,
        [workflowId]: {
          runId: null,
          status: 'pending',
          node_results_json: {},
          output_json: null,
          error_message: null,
        },
      }));
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE}/workflows/${workflowId}/runs`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              trigger: 'manual',
              input_values: inputValues,
            }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setActiveRuns((prev) => ({
          ...prev,
          [workflowId]: {
            ...(prev[workflowId] || {}),
            runId: data.run_id,
          },
        }));
        // Honour a cancel that was requested while runId was null
        if (pendingCancelRef.current[workflowId]) {
          delete pendingCancelRef.current[workflowId];
          await fetch(
            `${API_BASE}/workflows/${workflowId}/runs/${data.run_id}/cancel`,
            { method: 'POST', credentials: 'include' },
          ).catch(() => {});
          stopStream(workflowId);
          setActiveRuns((prev) => ({
            ...prev,
            [workflowId]: {
              ...(prev[workflowId] || {}),
              status: 'cancelled',
            },
          }));
          return data.run_id;
        }
        startStream(workflowId, data.run_id);
        return data.run_id;
      } catch (e) {
        setError(e.message);
        delete pendingCancelRef.current[workflowId];
        setActiveRuns((prev) => {
          const next = { ...prev };
          delete next[workflowId];
          return next;
        });
        return null;
      }
    },
    [startStream, stopStream],
  );

  /**
   * Re-execute a past run using its original input values.
   *
   * @param {string} workflowId
   * @param {string} runId - The historical run to replay.
   * @param {Function} [onNavigate] - Called immediately when the new
   *   run is created (e.g. to switch to the Run tab).
   * @returns {Promise<string|null>} new run_id or null on error
   */
  const rerunWorkflow = useCallback(
    async (workflowId, runId, onNavigate) => {
      setActiveRuns((prev) => ({
        ...prev,
        [workflowId]: {
          runId: null,
          status: 'pending',
          node_results_json: {},
          output_json: null,
          error_message: null,
        },
      }));
      setError(null);
      try {
        const res = await fetch(
          `${API_BASE}/workflows/${workflowId}/runs/${runId}/rerun`,
          { method: 'POST', credentials: 'include' },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        const data = await res.json();
        setActiveRuns((prev) => ({
          ...prev,
          [workflowId]: {
            ...(prev[workflowId] || {}),
            runId: data.run_id,
          },
        }));
        // Honour a cancel that was requested while runId was null
        if (pendingCancelRef.current[workflowId]) {
          delete pendingCancelRef.current[workflowId];
          await fetch(
            `${API_BASE}/workflows/${workflowId}/runs/${data.run_id}/cancel`,
            { method: 'POST', credentials: 'include' },
          ).catch(() => {});
          stopStream(workflowId);
          setActiveRuns((prev) => ({
            ...prev,
            [workflowId]: {
              ...(prev[workflowId] || {}),
              status: 'cancelled',
            },
          }));
          return data.run_id;
        }
        startStream(workflowId, data.run_id);
        if (onNavigate) onNavigate();
        return data.run_id;
      } catch (e) {
        setError(e.message);
        delete pendingCancelRef.current[workflowId];
        setActiveRuns((prev) => {
          const next = { ...prev };
          delete next[workflowId];
          return next;
        });
        return null;
      }
    },
    [startStream, stopStream],
  );

  /**
   * Cancel an in-flight run.  If the run_id is not yet known (the
   * POST /runs response has not arrived), the cancel is queued and
   * applied immediately once the run_id is available.
   *
   * @param {string} workflowId
   * @param {string|null} runId
   * @returns {Promise<boolean>}
   */
  const cancelRun = useCallback(
    async (workflowId, runId) => {
      if (!runId) {
        // Queue: will be honoured in runWorkflow/rerunWorkflow after
        // the run_id is returned from the server.
        pendingCancelRef.current[workflowId] = true;
        return true;
      }
      try {
        const res = await fetch(
          `${API_BASE}/workflows/${workflowId}/runs/${runId}/cancel`,
          { method: 'POST', credentials: 'include' },
        );
        if (!res.ok) return false;
        stopStream(workflowId);
        setActiveRuns((prev) => ({
          ...prev,
          [workflowId]: {
            ...(prev[workflowId] || {}),
            status: 'cancelled',
          },
        }));
        return true;
      } catch (_) {
        return false;
      }
    },
    [stopStream],
  );

  /**
   * Dismiss a run: close its SSE connection and remove it from
   * ``activeRuns`` so the UI returns to the empty state.
   *
   * @param {string} workflowId
   */
  const clearRunState = useCallback(
    (workflowId) => {
      stopStream(workflowId);
      setActiveRuns((prev) => {
        const next = { ...prev };
        delete next[workflowId];
        return next;
      });
    },
    [stopStream],
  );

  const listRuns = useCallback(async (workflowId, limit = 20) => {
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${workflowId}/runs?limit=${limit}`,
        { credentials: 'include' },
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.runs || [];
    } catch (_) {
      return [];
    }
  }, []);

  /** Fetch all runs for the authenticated user across all workflows.
   *  Returns null on failure so callers can distinguish "no runs"
   *  from a fetch error and avoid wiping cached state. */
  const listUserRuns = useCallback(async (limit = 50) => {
    try {
      const res = await fetch(
        `${API_BASE}/workflows/runs?limit=${limit}`,
        { credentials: 'include' },
      );
      if (!res.ok) return null;
      const data = await res.json();
      return data.runs || [];
    } catch (_) {
      return null;
    }
  }, []);

  /** Give a run a custom display name. */
  const renameRun = useCallback(async (workflowId, runId, name) => {
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${workflowId}/runs/${runId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ name }),
        },
      );
      return res.ok;
    } catch (_) {
      return false;
    }
  }, []);

  /** Permanently delete a workflow run. */
  const deleteRun = useCallback(async (workflowId, runId) => {
    try {
      const res = await fetch(
        `${API_BASE}/workflows/${workflowId}/runs/${runId}`,
        { method: 'DELETE', credentials: 'include' },
      );
      return res.ok;
    } catch (_) {
      return false;
    }
  }, []);

  return {
    workflows,
    loadingWorkflows,
    saving,
    error,
    activeRuns,
    listWorkflows,
    createWorkflow,
    getWorkflow,
    saveWorkflow,
    deleteWorkflow,
    runWorkflow,
    rerunWorkflow,
    cancelRun,
    stopStream,
    clearRunState,
    listRuns,
    listUserRuns,
    renameRun,
    deleteRun,
    clearError: () => setError(null),
  };
}
